import { Product, ProductAttachment, ProductColorVariant, ProductDetail, ProductFeature } from '../models/product.model';

function pickStr(raw: Record<string, unknown>, ...keys: string[]): string | null {
  for (const key of keys) {
    const value = raw[key];
    if (value !== undefined && value !== null && String(value).trim()) {
      return String(value).trim();
    }
  }
  return null;
}

function pickNum(raw: Record<string, unknown>, ...keys: string[]): number | null {
  for (const key of keys) {
    const value = raw[key];
    if (value !== undefined && value !== null && value !== '') {
      const num = Number(value);
      if (!Number.isNaN(num)) return num;
    }
  }
  return null;
}

function pickBool(raw: Record<string, unknown>, ...keys: string[]): boolean {
  for (const key of keys) {
    const value = raw[key];
    if (value !== undefined && value !== null) return Boolean(value);
  }
  return false;
}

function normalizeAttachments(raw: unknown): ProductAttachment[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  return raw.map((item) => {
    const attachment = item as Record<string, unknown>;
    return {
      id: pickNum(attachment, 'id', 'Id') ?? 0,
      imageUrl: pickStr(attachment, 'imageUrl', 'ImageUrl') ?? '',
      sortOrder: pickNum(attachment, 'sortOrder', 'SortOrder') ?? 0,
    };
  });
}

function normalizeFeatures(raw: unknown): ProductFeature[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  return raw.map((item) => {
    const feature = item as Record<string, unknown>;
    return {
      id: pickNum(feature, 'id', 'Id') ?? 0,
      name: pickStr(feature, 'name', 'Name') ?? '',
      description: pickStr(feature, 'description', 'Description'),
      imageUrl: pickStr(feature, 'imageUrl', 'ImageUrl'),
    };
  });
}

function normalizeColorVariants(raw: unknown): ProductColorVariant[] | undefined {
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  return raw.map((item) => {
    const variant = item as Record<string, unknown>;
    const colorLabelAr = pickStr(variant, 'colorLabelAr', 'ColorLabelAr') ?? '';
    const colorLabelEn = pickStr(variant, 'colorLabelEn', 'ColorLabelEn') ?? '';
    const colorLabel =
      pickStr(variant, 'colorLabel', 'ColorLabel') ?? (colorLabelAr || colorLabelEn || '');
    return {
      id: pickNum(variant, 'id', 'Id') ?? 0,
      name: pickStr(variant, 'name', 'Name') ?? '',
      nameEn: pickStr(variant, 'nameEn', 'NameEn'),
      nameAr: pickStr(variant, 'nameAr', 'NameAr'),
      colorLabel,
      colorLabelEn,
      colorLabelAr,
      colorHex: pickStr(variant, 'colorHex', 'ColorHex'),
      imageUrl: pickStr(variant, 'imageUrl', 'ImageUrl'),
      isSelected: pickBool(variant, 'isSelected', 'IsSelected'),
      inStock: pickBool(variant, 'inStock', 'InStock'),
      price: pickNum(variant, 'price', 'Price') ?? 0,
      discountPrice: pickNum(variant, 'discountPrice', 'DiscountPrice'),
    };
  });
}

/** Normalizes API product payloads (camelCase or PascalCase) for storefront binding. */
export function normalizeProduct<T extends Product = Product>(raw: unknown): T {
  if (!raw || typeof raw !== 'object') {
    return {} as T;
  }

  const source = raw as Record<string, unknown>;
  const sku = pickStr(source, 'sku', 'SKU');
  const name = pickStr(source, 'name', 'Name') ?? '';
  const nameEn = pickStr(source, 'nameEn', 'NameEn');
  const nameAr = pickStr(source, 'nameAr', 'NameAr');
  const brandFromApi = pickStr(source, 'brand', 'Brand');
  const brand = brandFromApi || null;
  const stockQuantity = pickNum(source, 'stockQuantity', 'StockQuantity');
  const inStockFromApi = source['inStock'] ?? source['InStock'];
  const attachments = normalizeAttachments(source['attachments'] ?? source['Attachments']);

  const normalized: Product = {
    id: pickNum(source, 'id', 'Id') ?? 0,
    name,
    nameEn,
    nameAr,
    description: pickStr(source, 'description', 'Description'),
    warranty: pickStr(source, 'warranty', 'Warranty'),
    price: pickNum(source, 'price', 'Price') ?? 0,
    discountPrice: pickNum(source, 'discountPrice', 'DiscountPrice'),
    imageUrl: pickStr(source, 'imageUrl', 'ImageUrl'),
    videoUrl: pickStr(source, 'videoUrl', 'VideoUrl'),
    weightKg: pickNum(source, 'weightKg', 'WeightKg'),
    categoryId: pickNum(source, 'categoryId', 'CategoryId') ?? 0,
    categoryName: pickStr(source, 'categoryName', 'CategoryName') ?? '',
    categoryNameEn: pickStr(source, 'categoryNameEn', 'CategoryNameEn'),
    categoryNameAr: pickStr(source, 'categoryNameAr', 'CategoryNameAr'),
    inStock:
      inStockFromApi !== undefined && inStockFromApi !== null
        ? Boolean(inStockFromApi)
        : stockQuantity == null || stockQuantity > 0,
    stockQuantity,
    sku,
    brand,
    fastShipment: pickBool(source, 'fastShipment', 'FastShipment'),
    offersFreeShipping: pickBool(source, 'offersFreeShipping', 'OffersFreeShipping'),
    isBestSeller: pickBool(source, 'isBestSeller', 'IsBestSeller'),
    newFlag: pickBool(source, 'newFlag', 'NewFlag'),
    newFlagExpiresAt: pickStr(source, 'newFlagExpiresAt', 'NewFlagExpiresAt'),
    isNew:
      source['isNew'] !== undefined || source['IsNew'] !== undefined
        ? pickBool(source, 'isNew', 'IsNew')
        : undefined,
    tag: pickStr(source, 'tag', 'Tag'),
    tagEn: pickStr(source, 'tagEn', 'TagEn'),
    isActive:
      source['isActive'] !== undefined || source['IsActive'] !== undefined
        ? pickBool(source, 'isActive', 'IsActive')
        : true,
    isInWishlist: pickBool(source, 'isInWishlist', 'IsInWishlist'),
    attachments,
  };

  const detail: ProductDetail = {
    ...normalized,
    isInWishlist: pickBool(source, 'isInWishlist', 'IsInWishlist'),
    isInCart: pickBool(source, 'isInCart', 'IsInCart'),
    cartQuantity: pickNum(source, 'cartQuantity', 'CartQuantity') ?? 0,
    features: normalizeFeatures(source['features'] ?? source['Features']),
    colorVariants: normalizeColorVariants(source['colorVariants'] ?? source['ColorVariants']),
  };

  return detail as unknown as T;
}

export function normalizeProductList(raw: unknown): Product[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((item) => normalizeProduct(item));
}
